import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import OrphionMark from "@/components/OrphionMark";

const YEAR = new Date().getFullYear();

export const Footer = () => {
  return (
    <footer className="relative bg-[var(--bg-2)] border-t border-[var(--line-strong)] pt-24 pb-10 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-12 pb-16">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <OrphionMark size={28} className="text-[var(--accent)]" />
              <p className="overline mb-0">Let&apos;s build your roadmap</p>
            </div>
            <h3 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-[var(--ink)] max-w-lg">
              Talent meets structure.
              <br />
              <span className="font-serif-accent text-[var(--accent)]">
                Chaos meets rhythm.
              </span>
            </h3>
            <p className="mt-6 max-w-md text-[var(--ink-70)] leading-relaxed">
              Prefer email or phone? We do the entire first conversation in the
              enquiry form — quicker for you, faster for us to route the right
              partner to your brief.
            </p>
            <Link
              to="/contact"
              className="btn-pill mt-10"
              data-testid={FOOTER.emailLink}
            >
              <span>Start with the form</span>
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="overline mb-4">Explore</p>
            <ul className="space-y-3">
              {[
                { to: "/gcc-support", label: "GCC Support" },
                { to: "/ai-native-it-services", label: "AI-Native IT Services" },
                { to: "/services", label: "Services" },
                { to: "/how-we-work", label: "How We Work" },
                { to: "/case-studies", label: "Case Studies" },
                { to: "/jobs", label: "Jobs" },
                { to: "/insights", label: "Insights" },
                { to: "/about", label: "About & Leadership" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="link-slide text-[var(--ink-80)] hover:text-[var(--ink)]"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline mb-4">Entity</p>
            <p className="text-[var(--ink-80)] leading-relaxed text-sm">
              Orphion Global HR is a brand of{" "}
              <span className="text-[var(--ink)]">
                Nivyan Business Solutions LLP
              </span>
              .
            </p>
            <p className="text-[var(--ink-70)] mt-3 text-sm">
              Bengaluru, India · Working globally.
            </p>
            <a
              href="mailto:sales@orphionglobalhr.com"
              className="link-slide text-[var(--ink-80)] hover:text-[var(--ink)] text-sm block mt-3"
              data-testid="footer-contact-email"
            >
              sales@orphionglobalhr.com
            </a>
            <a
              href="https://www.linkedin.com/company/orphion-global-hr"
              target="_blank"
              rel="noopener noreferrer"
              className="link-slide text-[var(--ink-80)] hover:text-[var(--ink)] text-sm block mt-2"
              data-testid="footer-linkedin"
            >
              LinkedIn ↗
            </a>
            <p className="overline mb-3 mt-8">Legal</p>
            <ul className="space-y-2">
              {[
                { to: "/privacy", label: "Privacy Policy" },
                { to: "/terms", label: "Terms of Use" },
                { to: "/cookies", label: "Cookie Policy" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="link-slide text-sm text-[var(--ink-70)] hover:text-[var(--ink)]"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.2 }}
          className="pt-6"
        >
          <div
            className="wordmark-huge select-none leading-[0.85]"
            data-testid={FOOTER.wordmark}
          >
            ORPHION
            <span className="font-serif-accent italic text-[var(--accent)] normal-case ml-2 md:ml-4">
              global hr
            </span>
          </div>
        </motion.div>

        <div className="mt-10 pt-6 border-t border-[var(--line-strong)] flex flex-col md:flex-row justify-between gap-4 text-xs uppercase tracking-[0.22em] text-[var(--ink-50)]">
          <p>© {YEAR} Nivyan Business Solutions LLP · All rights reserved</p>
          <p>Recruitment · HR Consulting · Employer Branding</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
